import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PipeTransform } from '@angular/core';
import { ChargingStationsPowerConsumption } from '@dto/chargingStationsPowerConsumption';
import { CustomerDto } from '@dto/customerDto';
import { CustomerNoteDto } from '@dto/customerNoteDto';
import { PatchCustomerNote } from '@dto/httpPatch/patchCustomerNote';
import { MasterUserOfCustomer } from '@dto/masterUserOfCustomer';
import { SubscriptionDetailDto } from '@dto/subscriptionDetailDto';
import { UserDto } from '@dto/userDto';
import { Customer } from '@models/customer';
import { CustomerNote } from '@models/customerNote';
import { CustomerPermission } from '@models/customerPermission';
import { Observable } from 'rxjs';
import { CustomersTableEntry } from '../../dto/customersTableEntry';
import { Setting } from '../../models/setting';
import { EntityService } from './entity.service';
import { EmspRequestDto } from '../../dto/emspRequestDto';

@Injectable({
  providedIn: 'root'
})
export class CustomersService extends EntityService {

  public entityRoute = "customers";

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    super(http, baseUrl);
  }

  matches(customer: Customer, term: string, pipe: PipeTransform) {
    return false;
  }

  getCustomer(customerId: number): Observable<CustomerDto> {
    return this.http.get<CustomerDto>(this.baseUrl + 'api/Customers/' + customerId);
  }

  getSelectedCustomer(): Observable<CustomerDto> {
    return this.http.get<CustomerDto>(this.baseUrl + 'api/CurrentCustomer');
  }

  getCustomerByName(name: string): Promise<CustomerDto> {
    return this.http.get<CustomerDto>(this.baseUrl + 'api/Customers/find/' + name).toPromise();
  }

  getSettings(customerId: number): Observable<Setting> {
    return this.http.get<Setting>(this.baseUrl + 'api/Customers/' + customerId + '/settings')
  }

  getCustomerNotes(customerId: number): Observable<CustomerNoteDto[]> {
    return this.http.get<CustomerNoteDto[]>(this.baseUrl + 'api/Customers/' + customerId + '/notes');
  }

  createCustomerNote(customerId: number, customerNote: CustomerNote): Observable<number> {
    return this.http.post<number>(this.baseUrl + 'api/Customers/' + customerId + '/note', customerNote);
  }

  patchCustomerNote(customerId: number, noteId: number, patchCustomerNote: PatchCustomerNote): Observable<number> {
    return this.http.patch<number>(this.baseUrl + 'api/Customers/notes/' + noteId, patchCustomerNote);
  }

  deleteCustomerNote(noteId: number): Observable<CustomerNote> {
    return this.http.delete<CustomerNote>(this.baseUrl + 'api/Customers/notes/' + noteId);
  }

  getCustomerPermissions(customerId: number): Observable<CustomerPermission[]> {
    return this.http.get<CustomerPermission[]>(this.baseUrl + 'api/Customers/' + customerId + '/permissions');
  }

  async getCustomerPermissionsAsync(customerId: number): Promise<CustomerPermission[]> {
    return await this.http.get<CustomerPermission[]>(this.baseUrl + 'api/Customers/' + customerId + '/permissions').toPromise();
  }

  async getSubscriptionTypeAsync(customerId: number): Promise<number> {
    return await this.http.get<number>(this.baseUrl + 'api/Customers/' + customerId + '/subscriptionType').toPromise();
  }

  getMasterUserOfCustomer(customerId: number): Observable<MasterUserOfCustomer> {
    return this.http.get<MasterUserOfCustomer>(this.baseUrl + 'api/Customers/' + customerId + '/masterUser');
  }

  enableEmsp(customerId: number, masterUserEmail: EmspRequestDto): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Customers/' + customerId + '/enable-emsp/', masterUserEmail)
  }

  disableEmsp(customerId: number, masterUserEmail: EmspRequestDto): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Customers/' + customerId + '/disable-emsp/', masterUserEmail)
  }

  getApplicationFeeForEmsp(customerId: number, id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/Customers/' + customerId + '/applicationFee/'+ id)
  }

  async getMasterUserOfCustomerAsync(customerId: number): Promise<MasterUserOfCustomer> {
    return await this.http.get<MasterUserOfCustomer>(this.baseUrl + 'api/Customers/' + customerId + '/masterUser').toPromise();
  }

  checkIfMasterUserOfCustomer(customerId: number, userId: string): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/Customers/' + customerId + '/isMasterUser/' + userId);
  }

  updateCustomerPermissions(customerId: number, customerPermissions: CustomerPermission[]): Observable<CustomerPermission[]> {
    return this.http.put<CustomerPermission[]>(this.baseUrl + 'api/Customers/' + customerId + '/permissions', customerPermissions);
  }

  patchSubscriptionType(customerId: number, typeId: number): Observable<Customer[]> {
    return this.http.patch<Customer[]>(this.baseUrl + 'api/Customers/' + customerId + '/subscriptionType/' + typeId, null);
  }

  getSubscriptionDetailsOfCustomer(customerId: number): Observable<SubscriptionDetailDto> {
    return this.http.get<SubscriptionDetailDto>(this.baseUrl + 'api/Customers/' + customerId + '/SubscriptionDetails/');
  }

  getCountofCustomers(): Observable<number> {
    return this.http.get<number>(this.baseUrl + 'api/Customers/count')
  }

  invite(customerId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/customers/invite/${customerId}`, payload);
  }

  invitefromoperator(customerId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/customers/invitefromoperator/${customerId}`, payload);
  }

  search(searchTerm: string): Observable<CustomersTableEntry[]> {
    return this.http.get<CustomersTableEntry[]>(`${this.baseUrl}api/Customers/search/${searchTerm}`);
  }
}
